/* global AppSettings */
import Mustache from 'mustache';
import CSRCountryCollection from 'components/csrFactoryMap/models/CSRCountryCollection';
import CSRLabelModel from 'components/csrFactoryMap/models/CSRLabelModel';
import CSRMapView from 'components/csrFactoryMap/views/CSRMapView';
import CSRFactoryMapLegend from 'components/csrFactoryMap/views/CSRFactoryMapLegend';
import CSRDetailCountryView from 'components/csrFactoryMap/views/CSRDetailCountryView';
import CSRDetailFactoryView from 'components/csrFactoryMap/views/CSRDetailFactoryView';
import ProductTypes from 'components/csrFactoryMap/utils/ProductTypes';
import template from './views/templates/mapui.html';
import createLogger from 'components/logger/Logger';

const Logger = createLogger('CSRFactoryMap');
const endpoint = AppSettings.endpoints.supplyChain;

export default class CSRFactoryMap {
  constructor(options) {
    this.el = options.el;
    this.factoryId = options.factoryId;
    this.isStatic = options.isStatic;
    this.initialize();
  }

  async initialize() {
    await import(/* webpackChunkName: "import--ammap" */ 'ammap');
    await import(/* webpackChunkName: "import--ammap-theme-light" */ 'ammap.theme.light');
    await import(/* webpackChunkName: "import--worldHigh" */ './worldHigh');
    CSRLabelModel.parseData(this.el.dataset.labels);
    this.createTemplate();
  }

  onInitialized() {
    this.dataInitialized = true;
    this.setupLegend();
    this.setupEventListeners();
    this.checkFactorySelect();
  }

  onMapInitialized() {
    this.mapInitialized = true;
    this.checkFactorySelect();

    const dialog = document.querySelector('.js-dialog');

    if (!dialog) {
      return;
    }

    this.setFocus(dialog);
  }

  setFocus(container) {
    const elements = container.querySelectorAll(
      'a[href], area[href], button, input:not([type="hidden"]), select, textarea, [tabindex="0"]:not(.js-detail-back)'
    );
    const visualElements = [...elements].filter(
      element =>
        window.getComputedStyle(element).display !== 'none' &&
        window.getComputedStyle(element).visibility !== 'hidden' &&
        element.disabled !== true
    );

    if (!visualElements.length) {
      return;
    }

    visualElements[0].focus();
  }

  fetchData() {
    this.countryCollection = new CSRCountryCollection({
      endpoint,
    });
    this.countryCollection
      .fetchData()
      .then(() => this.onInitialized())
      .catch(error => Logger.error(error));
  }

  checkFactorySelect() {
    if (this.dataInitialized && this.mapInitialized && this.factoryId) {
      this.factorySelect(this.factoryId, true);
    }
  }

  createTemplate() {
    const data = CSRLabelModel.getData();
    new ProductTypes({
      endpoint,
    })
      .fetchData()
      .then(productTypes => {
        data.product_types = productTypes;
        this.template = Mustache.render(template, data);
        this.renderTemplate();
      })
      .catch(error => Logger.error(error));
  }

  renderTemplate() {
    this.el.insertAdjacentHTML('afterbegin', this.template);
    this.fetchData();
    this.cacheSelectors();
    this.initMap();
    this.checkStatic();
  }

  cacheSelectors() {
    this.ui = {
      mapContainer: this.el.querySelectorAll('.js-map-container')[0],
      detail: this.el.querySelectorAll('.js-detail-container')[0],
      legend: this.el.querySelectorAll('.js-legend')[0],
    };
  }

  checkStatic() {
    const isStatic = this.isStatic === undefined ? false : this.isStatic;
    const csrMap = this.el.querySelector('.js-csr-map-main');
    csrMap.classList.toggle('csr-map--static', isStatic);
  }

  initMap() {
    this.map = new CSRMapView({ el: this.ui.mapContainer, collection: this.countryCollection });
    this.map.addListener('csr:map:initialized', () => this.onMapInitialized());
  }

  setupLegend() {
    const options = {
      el: this.ui.legend,
    };

    this.legend = new CSRFactoryMapLegend(options);
  }

  setupEventListeners() {
    this.onNavigateBack = () => this.navigateBack();
    this.onFactorySelect = (id, isDeeplink) => this.factorySelect(id, isDeeplink);

    if (this.isStatic) {
      this.el.jq.on('click', '.js-detail-back', event => event.preventDefault());
    }
    if (!this.isStatic) {
      this.map.addListener('csr:country:select', model => this.onCountrySelect(model));
    }
    if (!this.isStatic) {
      this.legend.addListener('csrmap:legend:select', data => this.onProductSelected(data));
    }
  }

  setupDetailView(View, model, skipAnimation) {
    this.removeDetailView(true);
    this.detailView = new View({
      el: this.ui.detail,
      model,
      skipAnimation,
      isStatic: this.isStatic,
    });
    this.detailView.addListener('csrmap:navigate:back', this.onNavigateBack);
    this.detailView.addListener('csrmap:select:factory', this.onFactorySelect);
  }

  factorySelect(id, isDeeplink) {
    this.factorySelected = true;
    const data = this.countryCollection.getFactoryDataForId(id);
    this.setupDetailView(CSRDetailFactoryView, data.model, true);
    if (isDeeplink) {
      this.map.setCountry(data.country.data);
    }
    this.map.showGroup(`factory-${id}`);
  }

  navigateBack() {
    if (this.factorySelected && this.countryModel) {
      this.factorySelected = false;
      this.removeDetailView(true);
      this.setupDetailView(CSRDetailCountryView, this.countryModel, true);
      this.map.showGroup('country');
    } else {
      this.removeDetailView();
      this.legend.toggleState(true);
      this.map.reset();

      this.map.showGroup('global');
    }
  }

  removeDetailView(skipAnimation) {
    if (this.detailView) {
      this.detailView.removeListener('csrmap:navigate:back', this.onNavigateBack);
      this.detailView.removeListener('csrmap:select:factory', this.onFactorySelect);
      this.detailView.destroy(skipAnimation);
      this.detailView = null;
    }
  }

  onCountrySelect(model) {
    if (model) {
      this.countryModel = model;
      this.setupDetailView(CSRDetailCountryView, model);
      this.legend.toggleState(false);
      this.legend.clearActiveFilterButton();
    }
  }

  onProductSelected(data) {
    const countries = this.countryCollection.getCountriesForProduct(data.productId);
    this.map.showAvailableCountries(countries);
  }
}
